// Types for automatic purchase invoice (NF-e) search and processing

export type SefazStatus = 'autorizada' | 'cancelada' | 'denegada';
export type PendingInvoiceStatus = 'pending' | 'processing' | 'processed' | 'linked' | 'ignored';
export type ManifestationType = 'ciencia' | 'confirmacao' | 'desconhecimento' | 'nao_realizada';

export interface PendingPurchaseInvoice {
  id: string;
  company_id: string;
  /** tpAmb: 1 = produção, 2 = homologação (UNIQUE ternária company/ambiente/access_key) */
  ambiente: number;
  access_key: string;
  invoice_number: string | null;
  invoice_series: string | null;
  emission_date: string | null;
  emission_month: string | null;
  emitente_cnpj: string | null;
  emitente_razao_social: string | null;
  emitente_ie: string | null;
  emitente_uf: string | null;
  total_value: number | null;
  sefaz_status: SefazStatus;
  status: PendingInvoiceStatus;
  manifestation_status: ManifestationType | null;
  manifestation_date: string | null;
  /** D-144.4: XML no bucket privado dfe-documents ({company_id}/{ambiente}/{chave}.xml); NULL = só resumo */
  xml_storage_path: string | null;
  nsu: string | null;
  cfop_predominante: string | null;
  linked_purchase_id: string | null;
  fetched_at: string;
  processed_at: string | null;
  processed_by: string | null;
  created_at: string;
  updated_at: string;
}

/**
 * Espelho de public.dfe_sync_log (20260116000904 + emendas notes/CHECK ampliado/ambiente).
 * status CHECK = success | error | partial | rate_limit | not_configured ('pending' proibido).
 * Nullability real: só company_id é NOT NULL; registros legados têm ambiente NULL.
 */
export type DfeSyncStatus = 'success' | 'error' | 'partial' | 'rate_limit' | 'not_configured';

export interface DfeSyncLog {
  id: string;
  company_id: string;
  /** tpAmb 1|2; NULL = registro legado (pré-§I.144) */
  ambiente: number | null;
  last_nsu: string | null;
  last_sync_at: string | null;
  documents_found: number | null;
  documents_new: number | null;
  status: DfeSyncStatus | null;
  error_message: string | null;
  /** contadores do dfe-sync: ja_lancadas / pre_corte / eventos / ... */
  notes: string | null;
  sync_type: 'manual' | 'automatic' | null;
  created_at: string | null;
}

// CFOP to Nature of Operation mapping
export type PurchaseNature = 
  | 'revenda' 
  | 'industrializacao' 
  | 'uso_consumo' 
  | 'ativo_imobilizado' 
  | 'outros';

export const CFOP_NATURE_MAP: Record<string, PurchaseNature> = {
  // Revenda (Mercadoria para Revenda)
  '1102': 'revenda', '2102': 'revenda', // Compra para comercialização
  '1403': 'revenda', '2403': 'revenda', // Compra p/ comercialização em operação c/ ST
  '1117': 'revenda', '2117': 'revenda', // Compra p/ comercialização originada de encomenda
  '1118': 'revenda', '2118': 'revenda', // Compra para comercialização em operação MEI
  '1113': 'revenda', '2113': 'revenda', // Compra de mercadoria recebida anteriormente em consignação
  '1401': 'revenda', '2401': 'revenda', // Compra mercadoria entregue a futuro
  
  '1111': 'industrializacao', '2111': 'industrializacao', // Compra p/ industrialização originada de encomenda
  '1116': 'industrializacao', '2116': 'industrializacao', // Compra para industrialização em operação MEI
  '1120': 'industrializacao', '2120': 'industrializacao', // Compra para industrialização - regime drawback
  '1121': 'industrializacao', '2121': 'industrializacao', // Compra para industrialização - ZFM/ALC
  '1122': 'industrializacao', '2122': 'industrializacao', // Compra para industrialização - regime aduaneiro
  '1126': 'industrializacao', '2126': 'industrializacao', // Compra para utilização na prestação de serviço
  
  // Uso e Consumo (Material de Uso/Consumo)
  '1556': 'uso_consumo', '2556': 'uso_consumo', // Compra de material p/ uso ou consumo
  '1407': 'uso_consumo', '2407': 'uso_consumo', // Compra de mercadoria p/ uso ou consumo c/ ST
  '1557': 'uso_consumo', '2557': 'uso_consumo', // Transferência de material de uso ou consumo
  
  // Ativo Imobilizado (Máquinas, Equipamentos, Veículos)
  '1551': 'ativo_imobilizado', '2551': 'ativo_imobilizado', // Compra de bem p/ ativo imobilizado
  '1406': 'ativo_imobilizado', '2406': 'ativo_imobilizado', // Compra de bem p/ ativo imobilizado c/ ST
  '1552': 'ativo_imobilizado', '2552': 'ativo_imobilizado', // Transferência de bem do ativo imobilizado
  '1553': 'ativo_imobilizado', '2553': 'ativo_imobilizado', // Devolução de venda de bem do ativo imobilizado
  '1554': 'ativo_imobilizado', '2554': 'ativo_imobilizado', // Retorno de bem do ativo imobilizado remetido
  
  // Outros (Devolução, Remessa, etc.)
  '1201': 'outros', '2201': 'outros', // Devolução de venda de produção
  '1202': 'outros', '2202': 'outros', // Devolução de venda de mercadoria
  '1203': 'outros', '2203': 'outros', // Devolução de venda de energia elétrica
  '1204': 'outros', '2204': 'outros', // Devolução de venda de produção c/ ST
  '1205': 'outros', '2205': 'outros', // Anulação de valor relativo a aquisição
  '1206': 'outros', '2206': 'outros', // Anulação de valor relativo a prestação de serviço
  '1207': 'outros', '2207': 'outros', // Devolução de mercadoria adquirida para uso ou consumo
  '1208': 'outros', '2208': 'outros', // Devolução de mercadoria remetida para uso ou consumo
  '1209': 'outros', '2209': 'outros', // Devolução de mercadoria adquirida para ativo imobilizado
  '1410': 'outros', '2410': 'outros', // Devolução de venda de mercadoria adquirida c/ ST
  '1411': 'outros', '2411': 'outros', // Devolução de venda de mercadoria produzida c/ ST
  '1503': 'outros', '2503': 'outros', // Entrada de energia elétrica no estabelecimento
  '1504': 'outros', '2504': 'outros', // Entrada de energia elétrica para distribuição
  '1505': 'outros', '2505': 'outros', // Entrada de energia elétrica para consumo
  '1651': 'outros', '2651': 'outros', // Compra de combustível para comercialização
  '1652': 'outros', '2652': 'outros', // Compra de combustível para industrialização
  '1653': 'outros', '2653': 'outros', // Compra de combustível para uso ou consumo
  '1658': 'outros', '2658': 'outros', // Transferência de combustível
  '1659': 'outros', '2659': 'outros', // Devolução de venda de combustível
  '1660': 'outros', '2660': 'outros', // Devolução de combustível adquirido
  '1901': 'outros', '2901': 'outros', // Entrada para industrialização por encomenda
  '1902': 'outros', '2902': 'outros', // Retorno de mercadoria remetida para industrialização
  '1903': 'outros', '2903': 'outros', // Entrada de mercadoria remetida para industrialização
  '1904': 'outros', '2904': 'outros', // Retorno de remessa para venda fora do estabelecimento
  '1905': 'outros', '2905': 'outros', // Entrada de mercadoria recebida para depósito
  '1906': 'outros', '2906': 'outros', // Retorno de mercadoria remetida para depósito
  '1907': 'outros', '2907': 'outros', // Retorno simbólico de mercadoria remetida para depósito
  '1908': 'outros', '2908': 'outros', // Entrada de bem por conta de contrato de comodato
  '1909': 'outros', '2909': 'outros', // Retorno de bem remetido por conta de contrato de comodato
  '1910': 'outros', '2910': 'outros', // Entrada de bonificação, doação ou brinde
  '1911': 'outros', '2911': 'outros', // Entrada de amostra grátis
  '1912': 'outros', '2912': 'outros', // Entrada de mercadoria ou bem recebido para demonstração
  '1913': 'outros', '2913': 'outros', // Retorno de mercadoria ou bem remetido para demonstração
  '1914': 'outros', '2914': 'outros', // Retorno de mercadoria ou bem remetido para exposição
  '1915': 'outros', '2915': 'outros', // Entrada de mercadoria ou bem recebido para conserto
  '1916': 'outros', '2916': 'outros', // Retorno de mercadoria ou bem remetido para conserto
  '1917': 'outros', '2917': 'outros', // Entrada de mercadoria recebida em consignação
  '1918': 'outros', '2918': 'outros', // Devolução de mercadoria remetida em consignação
  '1919': 'outros', '2919': 'outros', // Devolução simbólica de mercadoria vendida
  '1920': 'outros', '2920': 'outros', // Entrada de embalagens retornáveis
  '1921': 'outros', '2921': 'outros', // Retorno de embalagens retornáveis
  '1922': 'outros', '2922': 'outros', // Lançamento de crédito de ativo imobilizado
  '1923': 'outros', '2923': 'outros', // Entrada de mercadoria em armazém geral
  '1924': 'outros', '2924': 'outros', // Entrada para industrialização por conta de ordem
  '1925': 'outros', '2925': 'outros', // Retorno de mercadoria remetida para industrialização
  '1926': 'outros', '2926': 'outros', // Lançamento relativo a faturamento simbólico
  '1931': 'outros', '2931': 'outros', // Lançamento efetuado pelo tomador do serviço
  '1932': 'outros', '2932': 'outros', // Aquisição de serviço de transporte
  '1933': 'outros', '2933': 'outros', // Aquisição de serviço tributado pelo ISSQN
  '1949': 'outros', '2949': 'outros', // Outra entrada de mercadoria ou prestação de serviço
};

// Get purchase nature from CFOP
export function getPurchaseNatureFromCfop(cfop: string | null): PurchaseNature | null {
  if (!cfop) return null;
  
  // Remove any non-numeric characters and get the 4-digit CFOP
  const cleanCfop = cfop.replace(/\D/g, '').slice(0, 4);
  
  return CFOP_NATURE_MAP[cleanCfop] || null;
}

// Purchase nature labels for UI
export const PURCHASE_NATURE_LABELS: Record<PurchaseNature, string> = {
  revenda: 'Compra para Comercialização (Revenda)',
  industrializacao: 'Compra para Industrialização (Matéria Prima)',
  uso_consumo: 'Uso e Consumo',
  ativo_imobilizado: 'Ativo Imobilizado',
  outros: 'Outros',
};

// Purchase nature descriptions
export const PURCHASE_NATURE_DESCRIPTIONS: Record<PurchaseNature, string> = {
  revenda: 'Mercadorias adquiridas para revenda direta ao consumidor ou outras empresas',
  industrializacao: 'Insumos e matérias-primas para transformação em novos produtos',
  uso_consumo: 'Materiais de escritório, limpeza, manutenção e uso diário da empresa',
  ativo_imobilizado: 'Bens duráveis com vida útil superior a 1 ano (máquinas, veículos, equipamentos)',
  outros: 'Devoluções, remessas, transferências e outras operações especiais',
};

// Whether the nature moves stock
export const NATURE_MOVES_STOCK: Record<PurchaseNature, boolean> = {
  revenda: true,
  industrializacao: true,
  uso_consumo: false,
  ativo_imobilizado: false,
  outros: false,
};
